import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    Drawer, List, ListItem, ListItemIcon, ListItemText, Toolbar, Typography, Box, Divider, Collapse, Badge
} from '@mui/material';

// ICONOS
import HomeIcon from '@mui/icons-material/Home';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import BiotechIcon from '@mui/icons-material/Biotech';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ScienceIcon from '@mui/icons-material/Science';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import MailIcon from '@mui/icons-material/Mail'; 
import PrintIcon from '@mui/icons-material/Print';
import SnippetFolderIcon from '@mui/icons-material/SnippetFolder';
import HistoryIcon from '@mui/icons-material/History';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HealingIcon from '@mui/icons-material/Healing'; // Icono para procedimientos
import AnalyticsIcon from '@mui/icons-material/Analytics';

const drawerWidth = 240;
const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout, user } = useAuth();
    
    const isAdmin = user?.role === 'administrador';
    const isAsistente = user?.role === 'asistente'; 
    const isClinicalStaff = user?.role === 'administrador' || user?.role === 'medico';

    const [isProcedimientosOpen, setIsProcedimientosOpen] = useState(false);
    const [isRecipesOpen, setIsRecipesOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const checkMessages = useCallback(async () => {
        if (!user?.id) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/messages/unread-count/${user.id}?t=${Date.now()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setUnreadCount(data.count);
            }
        } catch (e) { console.error("Error unread count"); }
    }, [user?.id]);

    useEffect(() => {
        checkMessages();
        const interval = setInterval(checkMessages, 60000); 
        return () => clearInterval(interval);
    }, [checkMessages]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const listItemSx = { py: 0.5 }; 

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: drawerWidth,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box' },
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Toolbar sx={{ backgroundColor: 'primary.main', justifyContent: 'center', boxShadow: 1, minHeight: '56px !important' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', letterSpacing: 1 }}>
                        Luiatro Health
                    </Typography>
                </Toolbar>

                {unreadCount > 0 && (
                    <Box sx={{ bgcolor: 'error.main', color: 'white', p: 1, textAlign: 'center', cursor: 'pointer', animation: 'pulse 2s infinite' }} onClick={() => navigate('/mailbox')}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>🔔 {unreadCount} MENSAJES NUEVOS</Typography>
                    </Box>
                )}
                
                <List sx={{ flexGrow: 1, pt: 1, overflowY: 'auto' }} dense>
                    
                    <ListItem button sx={listItemSx} onClick={() => navigate('/agenda')} selected={location.pathname === '/agenda'}>
                        <ListItemIcon><CalendarMonthIcon sx={{ color: '#1976d2' }} /></ListItemIcon> {/* Azul Primario */}
                        <ListItemText primary="Agenda Medica" />
                    </ListItem>

                    <ListItem button sx={listItemSx} onClick={() => navigate('/mailbox')} selected={location.pathname === '/mailbox'}>
                        <ListItemIcon><Badge badgeContent={unreadCount} color="error"><MailIcon sx={{ color: '#ff9800' }} /></Badge></ListItemIcon> {/* Naranja */}
                        <ListItemText primary="Buzon de Mensajes" />
                    </ListItem>

                    <ListItem button sx={listItemSx} onClick={() => navigate('/dashboard')} selected={location.pathname === '/dashboard'}>
                        <ListItemIcon><HomeIcon sx={{ color: '#5c6bc0' }} /></ListItemIcon> {/* Indigo */}
                        <ListItemText primary="Dashboard" />
                    </ListItem>

                    <ListItem button sx={listItemSx} onClick={() => navigate('/patients')} selected={location.pathname === '/patients'}>
                        <ListItemIcon><PeopleIcon sx={{ color: '#43a047' }} /></ListItemIcon> {/* Verde Esmeralda */}
                        <ListItemText primary={isAsistente ? "Admision y Triage" : "Pacientes"} />
                    </ListItem>

                    {isClinicalStaff && (
                        <>
                            {/* AGENTE CAPTURA */}
                            <ListItem button sx={listItemSx} onClick={() => navigate('/agent-launcher')} selected={location.pathname === '/agent-launcher'}>
                                <ListItemIcon><CameraAltIcon sx={{ color: '#d32f2f' }} /></ListItemIcon> {/* Rojo Intenso */}
                                <ListItemText primary="Agente Captura" />
                            </ListItem>

                            {/* VADEMECUM */}
                            <ListItem button sx={listItemSx} onClick={() => navigate('/vademecum')} selected={location.pathname === '/vademecum'}>
                                <ListItemIcon><MedicationLiquidIcon sx={{ color: '#8e24aa' }} /></ListItemIcon> {/* Púrpura */}
                                <ListItemText primary="Vademecum" />
                            </ListItem>
                            
                            {/* PARACLINICOS */}
                            <ListItem button sx={listItemSx} onClick={() => navigate('/paraclinicos')} selected={location.pathname === '/paraclinicos'}>
                                <ListItemIcon><BiotechIcon sx={{ color: '#00acc1' }} /></ListItemIcon> {/* Cian */}
                                <ListItemText primary="Paraclinicos (Carga)" />
                            </ListItem>
                            
                            {/* INFORMES */}
                            <ListItem button sx={listItemSx} onClick={() => navigate('/informes')} selected={location.pathname === '/informes'}>
                                <ListItemIcon><FolderSharedIcon sx={{ color: '#f57c00' }} /></ListItemIcon> {/* Naranja Oscuro */}
                                <ListItemText primary="Informes Medicos" />
                            </ListItem>

                            {/* ✅ PROCEDIMIENTOS (NUEVO ITEM AGREGADO) */}
                            <ListItem button sx={listItemSx} onClick={() => navigate('/reporte-procedimientos')} selected={location.pathname === '/reporte-procedimientos'}>
                                <ListItemIcon><HealingIcon sx={{ color: '#009688' }} /></ListItemIcon> {/* Teal / Verde Quirúrgico */}
                                <ListItemText primary="Reporte de Procedimientos" />
                            </ListItem>

                            {/* HISTORIA INTEGRAL */}
                            <ListItem button sx={listItemSx} onClick={() => navigate('/reports')} selected={location.pathname === '/reports'}>
                                <ListItemIcon><AssessmentIcon sx={{ color: '#3949ab' }} /></ListItemIcon> {/* Azul Marino */}
                                <ListItemText primary="Historia Integral" />
                            </ListItem>
                            
                            {/* GESTIÓN DOCUMENTAL */}
                            <ListItem button sx={listItemSx} onClick={() => navigate('/documents')} selected={location.pathname === '/documents'}>
                                <ListItemIcon><SnippetFolderIcon sx={{ color: '#795548' }} /></ListItemIcon> {/* Marrón */}
                                <ListItemText primary="Gestion Documental" />
                            </ListItem>

                            {/* RECIPES (DESPLEGABLE) */}
                            <ListItem button sx={listItemSx} onClick={() => setIsRecipesOpen(!isRecipesOpen)}>
                                <ListItemIcon><PrintIcon sx={{ color: '#8e24aa' }} /></ListItemIcon> {/* Verde Lima Oscuro */}
                                <ListItemText primary="Recipes e Impresion" />
                                {isRecipesOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItem>
                            <Collapse in={isRecipesOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding dense>
                                    <ListItem button sx={{ pl: 4 }} onClick={() => navigate('/recipes')}><ListItemIcon><MedicationLiquidIcon fontSize="small" sx={{ color: '#558b2f' }} /></ListItemIcon><ListItemText primary="Recipes Farmacologicos" /></ListItem>
                                    <ListItem button sx={{ pl: 4 }} onClick={() => navigate('/recipe-studies')}><ListItemIcon><ScienceIcon fontSize="small" sx={{ color: '#558b2f' }} /></ListItemIcon><ListItemText primary="Recipes Estudios" /></ListItem>
                                    <ListItem button sx={{ pl: 4 }} onClick={() => navigate('/paraclinicos/solicitud')}><ListItemIcon><BiotechIcon fontSize="small" sx={{ color: '#558b2f' }} /></ListItemIcon><ListItemText primary="Solicitud Examenes" /></ListItem>
                                </List>
                            </Collapse>

                            {/* CATÁLOGO (DESPLEGABLE) */}
                            <ListItem button sx={listItemSx} onClick={() => setIsProcedimientosOpen(!isProcedimientosOpen)}>
                                <ListItemIcon><MedicalServicesIcon sx={{ color: '#f57c00' }} /></ListItemIcon> {/* Gris Azulado */}
                                <ListItemText primary="Catálogo" />
                                {isProcedimientosOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItem>
                            <Collapse in={isProcedimientosOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding dense>
                                    <ListItem button sx={{ pl: 4 }} onClick={() => navigate('/procedimientos-medicos')}><ListItemIcon><MedicalServicesIcon fontSize="small" sx={{ color: '#607d8b' }} /></ListItemIcon><ListItemText primary="Procedimientos" /></ListItem>
                                    <ListItem button sx={{ pl: 4 }} onClick={() => navigate('/solicitud-estudios')}><ListItemIcon><ScienceIcon fontSize="small" sx={{ color: '#607d8b' }} /></ListItemIcon><ListItemText primary="Estudios" /></ListItem>
                                </List>
                            </Collapse>
                        </>
                    )}

                    {isAdmin && (
                        <ListItem button sx={listItemSx} onClick={() => navigate('/auditoria')} selected={location.pathname === '/auditoria'}>
                            <ListItemIcon><HistoryIcon sx={{ color: '#455a64' }} /></ListItemIcon> {/* Gris Oscuro */}
                            <ListItemText primary="Log de Envios" />
                        </ListItem>
                    )}

                    <Divider sx={{ my: 1 }} />
					
					{isAdmin && (
						<ListItem button sx={listItemSx} onClick={() => navigate('/stats')} selected={location.pathname === '/stats'}>
							<ListItemIcon><AnalyticsIcon sx={{ color: '#0288d1' }} /></ListItemIcon> {/* Azul Oceano */}
							<ListItemText primary="Estadísticas" />
						</ListItem>
                    )}

                    <ListItem button sx={listItemSx} onClick={() => navigate('/settings')} selected={location.pathname === '/settings'}>
                        <ListItemIcon><SettingsIcon sx={{ color: '#757575' }} /></ListItemIcon> {/* Gris Neutro */}
                        <ListItemText primary="Configuración" />
                    </ListItem>
                </List>
                
                <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '0.85rem' }}>{user?.name}</Typography>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{user?.role}</Typography>
                </Box>

                <ListItem button onClick={handleLogout} sx={{ bgcolor: '#ffebee', color: '#d32f2f', py: 1 }}>
                    <ListItemIcon><LogoutIcon color="error" /></ListItemIcon>
                    <ListItemText primary="Cerrar Sesion" />
                </ListItem>
            </Box>
            <style>{`@keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.7; } 100% { opacity: 1; } }`}</style>
        </Drawer>
    );
};

export default Sidebar;